import { useState } from "react";
import { useStore } from "../state/store";
import { LocalizedField } from "../ui/common";
import { LocaleSwitcher } from "../ui/LocaleSwitcher";
import { validateNamePart } from "../model/validation";
import type { GuideCategory, GuideEntry } from "../model/types";

export function GuidePanel() {
  const project = useStore((s) => s.project);
  const activeLocale = useStore((s) => s.ui.activeLocale);
  const updateGuideTitle = useStore((s) => s.updateGuideTitle);
  const updateGuideIntro = useStore((s) => s.updateGuideIntro);
  const setGiveOnFirstJoin = useStore((s) => s.setGiveOnFirstJoin);
  const giveOnFirstJoin = useStore((s) => s.project.guide.giveOnFirstJoin);
  const addCategory = useStore((s) => s.addCategory);

  // Collapse state: a uuid present in the set is EXPANDED (default collapsed).
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, uuid: string) => {
    const next = new Set(set);
    next.has(uuid) ? next.delete(uuid) : next.add(uuid);
    setter(next);
  };

  const expandAll = () => {
    setExpandedCats(new Set(project.guide.categories.map((c) => c.uuid)));
    setExpandedEntries(new Set(project.guide.categories.flatMap((c) => c.entries.map((e) => e.uuid))));
  };
  const collapseAll = () => {
    setExpandedCats(new Set());
    setExpandedEntries(new Set());
  };

  return (
    <div className="panel">
      <div className="list-header">
        <h2>Guidebook</h2>
        <div className="btn-row">
          <button className="btn" onClick={collapseAll}>
            Collapse all
          </button>
          <button className="btn" onClick={expandAll}>
            Expand all
          </button>
          <button className="btn primary" onClick={() => addCategory()}>
            + Category
          </button>
        </div>
      </div>
      <p className="hint">
        The homepage shows a button for each <strong>category</strong>; opening a category lists its{" "}
        <strong>entries</strong> (the items shown under it). Click a category or entry header to expand it.
      </p>
      <LocaleSwitcher />

      <section className="card">
        <LocalizedField
          label="Book title (also the guide item's name)"
          locale={activeLocale}
          value={project.guide.title}
          onChange={(title) => {
            for (const [locale, value] of Object.entries(title)) updateGuideTitle(locale, value);
          }}
        />
        <LocalizedField
          label="Homepage intro text"
          locale={activeLocale}
          multiline
          value={project.guide.intro}
          onChange={(intro) => {
            for (const [locale, value] of Object.entries(intro)) updateGuideIntro(locale, value);
          }}
        />
        <label className="checkbox-field">
          <input type="checkbox" checked={giveOnFirstJoin} onChange={(e) => setGiveOnFirstJoin(e.target.checked)} />
          <span>Give the guide book to each player once, on their first spawn</span>
        </label>
      </section>

      {project.guide.categories.map((cat, i) => (
        <CategoryEditor
          key={cat.uuid}
          category={cat}
          index={i}
          locale={activeLocale}
          expanded={expandedCats.has(cat.uuid)}
          onToggle={() => toggle(expandedCats, setExpandedCats, cat.uuid)}
          expandedEntries={expandedEntries}
          onToggleEntry={(uuid) => toggle(expandedEntries, setExpandedEntries, uuid)}
        />
      ))}
      {project.guide.categories.length === 0 && (
        <p className="muted">No categories yet. Add one to start building your guidebook.</p>
      )}
    </div>
  );
}

function CategoryEditor({
  category,
  index,
  locale,
  expanded,
  onToggle,
  expandedEntries,
  onToggleEntry,
}: {
  category: GuideCategory;
  index: number;
  locale: string;
  expanded: boolean;
  onToggle: () => void;
  expandedEntries: Set<string>;
  onToggleEntry: (uuid: string) => void;
}) {
  const namespace = useStore((s) => s.project.namespace);
  const categoryCount = useStore((s) => s.project.guide.categories.length);
  const updateCategory = useStore((s) => s.updateCategory);
  const moveCategory = useStore((s) => s.moveCategory);
  const removeCategory = useStore((s) => s.removeCategory);
  const addEntry = useStore((s) => s.addEntry);

  const idValid = validateNamePart(category.id) && category.id !== "main";

  return (
    <section className="card page-card">
      <div className="collapse-head">
        <button className="collapse-toggle" onClick={onToggle} aria-expanded={expanded}>
          <span className="chevron">{expanded ? "▾" : "▸"}</span>
          <span className="collapse-title">{category.title[locale] || category.id}</span>
          <span className="muted count-badge">{category.entries.length}</span>
        </button>
        <div className="btn-row">
          <button className="btn" disabled={index === 0} onClick={() => moveCategory(category.uuid, -1)}>
            ↑
          </button>
          <button className="btn" disabled={index === categoryCount - 1} onClick={() => moveCategory(category.uuid, 1)}>
            ↓
          </button>
          <button className="btn danger" onClick={() => removeCategory(category.uuid)}>
            Delete
          </button>
        </div>
      </div>

      {expanded && (
        <div className="collapse-body">
          <label className="field">
            <span className="field-label">Category id (name-part)</span>
            <input
              className={idValid ? "" : "invalid"}
              value={category.id}
              spellCheck={false}
              onChange={(e) => updateCategory(category.uuid, { id: e.target.value.trim() })}
            />
          </label>
          <div className="preview-line">
            Lang key root: <code>{namespace}.guide.{category.id || "?"}</code>
          </div>
          {!idValid && <p className="error-text">Id must match ^[a-z0-9_]+$ and cannot be "main".</p>}

          <LocalizedField
            label="Category title"
            locale={locale}
            value={category.title}
            onChange={(title) => updateCategory(category.uuid, { title })}
          />
          <LocalizedField
            label="Category intro (optional)"
            locale={locale}
            multiline
            value={category.body}
            onChange={(body) => updateCategory(category.uuid, { body })}
          />

          <div className="entries">
            <div className="entries-head">
              <span className="field-label">Entries ({category.entries.length})</span>
              <button className="btn primary" onClick={() => addEntry(category.uuid)}>
                + Entry
              </button>
            </div>
            {category.entries.map((entry, j) => (
              <EntryEditor
                key={entry.uuid}
                catUuid={category.uuid}
                entry={entry}
                index={j}
                count={category.entries.length}
                locale={locale}
                expanded={expandedEntries.has(entry.uuid)}
                onToggle={() => onToggleEntry(entry.uuid)}
              />
            ))}
            {category.entries.length === 0 && <p className="muted">No entries. Add one with “+ Entry”.</p>}
          </div>
        </div>
      )}
    </section>
  );
}

function EntryEditor({
  catUuid,
  entry,
  index,
  count,
  locale,
  expanded,
  onToggle,
}: {
  catUuid: string;
  entry: GuideEntry;
  index: number;
  count: number;
  locale: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const recipes = useStore((s) => s.project.recipes);
  const updateEntry = useStore((s) => s.updateEntry);
  const moveEntry = useStore((s) => s.moveEntry);
  const removeEntry = useStore((s) => s.removeEntry);

  const idValid = validateNamePart(entry.id);

  return (
    <div className="entry-card">
      <div className="collapse-head">
        <button className="collapse-toggle" onClick={onToggle} aria-expanded={expanded}>
          <span className="chevron">{expanded ? "▾" : "▸"}</span>
          <span className="collapse-title">{entry.title[locale] || entry.id}</span>
        </button>
        <div className="btn-row">
          <button className="btn" disabled={index === 0} onClick={() => moveEntry(catUuid, entry.uuid, -1)}>
            ↑
          </button>
          <button className="btn" disabled={index === count - 1} onClick={() => moveEntry(catUuid, entry.uuid, 1)}>
            ↓
          </button>
          <button className="btn danger" onClick={() => removeEntry(catUuid, entry.uuid)}>
            ✕
          </button>
        </div>
      </div>

      {expanded && (
        <div className="collapse-body">
          <label className="field">
            <span className="field-label">Entry id</span>
            <input
              className={idValid ? "" : "invalid"}
              value={entry.id}
              spellCheck={false}
              onChange={(e) => updateEntry(catUuid, entry.uuid, { id: e.target.value.trim() })}
            />
          </label>
          <LocalizedField
            label="Title"
            locale={locale}
            value={entry.title}
            onChange={(title) => updateEntry(catUuid, entry.uuid, { title })}
          />
          <LocalizedField
            label="Body"
            locale={locale}
            multiline
            value={entry.body}
            onChange={(body) => updateEntry(catUuid, entry.uuid, { body })}
          />
          <label className="field">
            <span className="field-label">Show recipe on this entry (optional)</span>
            <select
              value={entry.recipeId ?? ""}
              onChange={(e) => updateEntry(catUuid, entry.uuid, { recipeId: e.target.value || undefined })}
            >
              <option value="">(none)</option>
              {recipes.map((r) => (
                <option key={r.uuid} value={r.id}>
                  {r.id}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
    </div>
  );
}
